import React, { useEffect, useState } from 'react';
import { CircularProgress, Alert, Box, Stack, Typography, Chip, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRowHeightParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { exportToCsv } from './ReportLayout';
import { reportsApi } from '../../api/reports';
import DesignPreviewDialog from '../../components/DesignPreviewDialog';
import designApi from '../../api/design';
import { designCuttingApi } from '../../services/api';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

interface PendingJobWork {
  vendor: string;
  job_type: string;
  aging_days: number;
}

interface DupattaStep {
  job_type: string;
  status: 'IN_WORK' | 'PARTIAL' | 'COMPLETED';
}

const DUPATTA_STEP_CONFIG: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'success' }> = {
  IN_WORK:   { label: 'In Process', color: 'info' },
  PARTIAL:   { label: 'Partial',    color: 'warning' },
  COMPLETED: { label: 'Done',       color: 'success' },
};

/** Mini pipeline for dupatta fabric processing steps */
function DupattaPipeline({ steps }: { steps: DupattaStep[] | null }) {
  // null = not a SUIT product
  if (steps === null) return null;
  // SUIT but no steps created yet
  if (steps.length === 0) {
    return (
      <Chip label="Not Started" color="error" size="small" variant="outlined"
        sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
    );
  }
  return (
    <Stack spacing={0.5} sx={{ py: 0.5, width: '100%' }}>
      {steps.map((step, i) => {
        const cfg = DUPATTA_STEP_CONFIG[step.status] ?? { label: step.status, color: 'default' as const };
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 500, color: 'text.secondary', flexShrink: 0 }}>
              {step.job_type}
            </Typography>
            <Chip label={cfg.label} color={cfg.color} size="small" variant="filled"
              sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.75 } }} />
          </Box>
        );
      })}
    </Stack>
  );
}

/** Returns colour based on aging days */
function agingColor(days: number): 'success' | 'warning' | 'error' {
  if (days <= 7) return 'success';
  if (days <= 15) return 'warning';
  return 'error';
}

/** A single job-work pill with wrapping text */
function JobWorkPill({ jw }: { jw: PendingJobWork }) {
  const color = agingColor(jw.aging_days);
  return (
    <Tooltip title={`${jw.vendor} · ${jw.job_type} · ${jw.aging_days}d ago`} placement="left">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          px: 1,
          py: 0.4,
          borderRadius: 2,
          bgcolor: `${color}.50`,
          border: '1px solid',
          borderColor: `${color}.200`,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Vendor + job type stacked, fully wrapping */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: `${color}.800`,
              display: 'block',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.3,
            }}
          >
            {jw.vendor}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.3,
            }}
          >
            {jw.job_type}
          </Typography>
        </Box>
        {/* Aging badge pinned to the right */}
        <Chip
          label={`${jw.aging_days}d`}
          color={color}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            flexShrink: 0,
            alignSelf: 'flex-start',
            mt: 0.1,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Box>
    </Tooltip>
  );
}

export default function PendingBarcodingReport() {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDesign, setPreviewDesign] = useState<any>(null);
  const [previewDesignNo, setPreviewDesignNo] = useState<string | null>(null);
  const [topDesign, setTopDesign] = useState<any>(null);
  const [bottomDesign, setBottomDesign] = useState<any>(null);
  const [loadingCuttingData, setLoadingCuttingData] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getPendingBarcoding();
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImageClick = async (row: any) => {
    setPreviewOpen(true);
    setPreviewDesignNo(row.design_no);
    setPreviewDesign(null);
    setTopDesign(null);
    setBottomDesign(null);
    setLoadingCuttingData(true);
    try {
      if (row.design_id) {
        const full = await designApi.getById(row.design_id as number);
        setPreviewDesign(full.data);
      }
    } catch {}
    try {
      const list = await designCuttingApi.getAll();
      const top = Array.isArray(list)
        ? list.find((d: any) => d.design_no === row.design_no && d.garment_part === 'TOP')
        : null;
      const bottom = Array.isArray(list)
        ? list.find((d: any) => d.design_no === row.design_no && d.garment_part === 'BOTTOM')
        : null;
      setTopDesign(top || null);
      setBottomDesign(bottom || null);
    } catch {}
    setLoadingCuttingData(false);
  };

  const handleDesignNoClick = (designNo: string) => {
    navigate(`/designs?search=${encodeURIComponent(designNo)}&preview=true`);
  };

  const columns: GridColDef[] = [
    {
      field: 'design',
      headerName: 'Design',
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5, width: '100%' }}>
          {/* Clickable image */}
          <Box
            sx={{ cursor: 'pointer', flexShrink: 0 }}
            onClick={() => handleImageClick(params.row)}
          >
            {params.row.image_url ? (
              <img
                src={params.row.image_url}
                alt={params.row.design_no}
                style={{
                  width: 70,
                  height: 100,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #eee',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 70,
                  height: 100,
                  borderRadius: 1,
                  border: '1px dashed #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              >
                N/A
              </Box>
            )}
          </Box>

          {/* Clickable design no + product type — wrapping, not truncated */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                lineHeight: 1.4,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => handleDesignNoClick(params.row.design_no)}
            >
              {params.row.design_no}
            </Typography>
            {params.row.product_type && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {params.row.product_type}
              </Typography>
            )}
          </Box>
        </Stack>
      ),
    },
    { field: 'stitched', headerName: 'Stitched', type: 'number', width: 90 },
    { field: 'pressed', headerName: 'Pressed', type: 'number', width: 90 },
    { field: 'qc_passed', headerName: 'QC Passed', type: 'number', width: 95 },
    { field: 'permanent_damaged', headerName: 'Perm. Damaged', type: 'number', width: 115 },
    { field: 'barcoded', headerName: 'Barcoded', type: 'number', width: 90 },
    {
      field: 'pending_barcoding',
      headerName: 'Pending',
      type: 'number',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value > 0 ? 'warning' : 'success'}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      field: 'pending_job_works',
      headerName: 'Pending Job Works',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const jobs: PendingJobWork[] = params.row.pending_job_works || [];
        if (jobs.length === 0) return null;
        return (
          <Stack
            spacing={0.75}
            sx={{
              py: 0.75,
              width: '100%',
              // Allow the stack to grow taller than the default cell height
              alignSelf: 'flex-start',
            }}
          >
            {jobs.map((jw, idx) => (
              <JobWorkPill key={idx} jw={jw} />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'dupatta_pipeline',
      headerName: 'Dupatta Process',
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const steps: DupattaStep[] | null = params.row.dupatta_pipeline ?? null;
        return <DupattaPipeline steps={steps} />;
      },
    },
    {
      field: 'last_stitching_completed',
      headerName: 'Stitching Done',
      width: 125,
      valueFormatter: (value: any) =>
        value ? new Date(value).toLocaleDateString('en-IN') : '-',
    },
  ];

  const totalPending = data.reduce((s, d) => s + (d.pending_barcoding || 0), 0);
  const totalStitched = data.reduce((s, d) => s + (d.stitched || 0), 0);
  const totalBarcoded = data.reduce((s, d) => s + (d.barcoded || 0), 0);

  /**
   * Dynamic row height.
   * Base: 120px (fits the image thumbnail).
   * Each job-work pill is roughly 44px (two lines of text + padding).
   * If multiple pills, extra rows add 48px each.
   */
  const getRowHeight = (params: GridRowHeightParams) => {
    const jobs: PendingJobWork[] = (params.model as any).pending_job_works || [];
    const dupattaSteps: DupattaStep[] = (params.model as any).dupatta_pipeline || [];
    const jobExtra = Math.max(0, jobs.length - 1) * 48;
    // Each dupatta step row is ~26px; first step fits in the base height
    const dupattaExtra = Math.max(0, dupattaSteps.length - 1) * 26;
    return 120 + Math.max(jobExtra, dupattaExtra);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Pending Barcoding Report
        </Typography>
        <Chip label={`${data.length} Designs`} variant="outlined" />
        <Chip label={`Pending: ${totalPending}`} color="warning" />
        <Chip label={`Stitched: ${totalStitched}`} color="info" variant="outlined" />
        <Chip label={`Barcoded: ${totalBarcoded}`} color="success" variant="outlined" />
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={() =>
            exportToCsv(
              'pending-barcoding.csv',
              data.map(({ image_url, pending_job_works, ...rest }) => rest)
            )
          }
          disabled={loading || data.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <CircularProgress />
        ) : (
          <DataGrid
            rows={data}
            columns={columns}
            getRowId={(row) => row.design_no}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={getRowHeight}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              '& .MuiDataGrid-cell': {
                // Top-align so pills stack from the top
                alignItems: 'flex-start',
                // Must override overflow:hidden so wrapping text is visible
                overflow: 'visible',
              },
              '& .MuiDataGrid-cell--withRenderer': {
                overflow: 'visible',
              },
            }}
          />
        )}
      </Paper>

      <DesignPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        design={previewDesign}
        designNo={previewDesignNo || undefined}
        topDesign={topDesign}
        bottomDesign={bottomDesign}
        loadingCuttingData={loadingCuttingData}
        hideCost
      />
    </Box>
  );
}
